import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { templatesApi, DOC_TYPE_LABEL, getDocTypeLabel, type BuiltinDocType, type DocType, type DocumentTemplate } from "../api.js";
import { tenantDocumentTypesApi } from "../api/tenantDocumentTypesApi.js";
import { useAIStore } from "../stores/aiStore.js";
import { useEditorStore } from "../stores/editorStore.js";
import { toast } from "../components/Toast.js";
import { Button } from "../components/ui/Button.js";
import { useScreenView } from "../lib/analytics.js";
import { useState } from "react";

// 組み込み7書類種別の表示順（tenant_document_types の display_order とは独立に、
// 従来のタブ表示順を維持する）
const DOC_TYPES: BuiltinDocType[] = [
  "judicial_invoice",
  "administrative_invoice",
  "surveyor_invoice",
  "estimate",
  "guarantee_invoice",
  "land_calculation",
  "building_calculation",
];

export function TemplatesPage() {
  useScreenView("templates");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openAI = useAIStore((state) => state.openPanel);
  const closeAI = useAIStore((state) => state.closePanel);
  const draft = useEditorStore((state) => state.draft);
  const draftTemplateId = useEditorStore((state) => state.templateId);
  const hasUnsavedDraft = useEditorStore((state) => state.isDirty && !!state.draft);
  const clearDraft = useEditorStore((state) => state.clearDraft);
  const [activeType, setActiveType] = useState<DocType>("judicial_invoice");

  const { data, isLoading, error } = useQuery({
    queryKey: ["templates", activeType],
    queryFn: () => templatesApi.list(activeType),
    retry: false,
  });

  // 書類種別タブ一覧は tenant_document_types（組み込み＋カスタム）に一本化する
  // （設計書: docs/design/document-type-master-phase2.md §8.4。localStorage ベースの
  // 「カスタム書類種別」タブは撤去。新規カスタム種別の追加は /settings/professions から行う）
  const { data: docTypesData } = useQuery({
    queryKey: ["tenant-document-types"],
    queryFn: () => tenantDocumentTypesApi.list(),
  });
  const allDocTypes = docTypesData?.data ?? [];
  const builtinOptions = DOC_TYPES.map((dt) => ({
    id: dt as DocType,
    label: allDocTypes.find((d) => d.doc_type === dt)?.label ?? DOC_TYPE_LABEL[dt],
  }));
  const customOptions = allDocTypes
    .filter((d) => !d.is_builtin)
    .sort((a, b) => a.display_order - b.display_order)
    .map((d) => ({ id: d.doc_type as DocType, label: d.label }));
  const docTypeOptions = [...builtinOptions, ...customOptions];

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => templatesApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast("既定テンプレートを変更しました");
    },
    onError: (err) => toast(err instanceof Error ? err.message : String(err), "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id),
    onSuccess: (_result, id) => {
      if (draftTemplateId === id) clearDraft();
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast("テンプレートを削除しました");
    },
    onError: (err) => toast(err instanceof Error ? err.message : String(err), "error"),
  });

  const templates: DocumentTemplate[] = data?.data ?? [];

  function goNew() {
    closeAI();
    navigate(`/settings/templates/new?type=${activeType}`);
  }

  function goNewWithAI() {
    navigate(`/settings/templates/new?type=${activeType}`);
    openAI();
  }

  function resumeDraft() {
    navigate(`/settings/templates/${draftTemplateId ?? "new"}?type=${activeType}`);
  }

  function discardDraft() {
    if (!window.confirm("作成中のテンプレートを削除しますか？未保存の内容は元に戻せません。")) return;
    clearDraft();
    toast("作成中のテンプレートを削除しました");
  }

  function deleteTemplate(template: DocumentTemplate) {
    const defaultText = template.is_default === 1 ? "既定テンプレートです。" : "";
    if (!window.confirm(`「${template.name}」を削除しますか？${defaultText}削除すると元に戻せません。`)) return;
    deleteMutation.mutate(template.id);
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0, color: "#0f172a" }}>テンプレート管理</h1>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 5 }}>AI生成、PDF取り込み、手動グリッド編集をここから始めます。</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={goNewWithAI}>AIに作ってもらう</Button>
          <Button variant="primary" onClick={goNew}>新規作成</Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 20, overflowX: "auto", alignItems: "center" }}>
        {docTypeOptions.map((type) => (
          <button
            key={type.id}
            onClick={() => setActiveType(type.id)}
            style={{
              padding: "9px 14px",
              fontSize: 13,
              fontWeight: activeType === type.id ? 800 : 600,
              border: "none",
              borderBottom: activeType === type.id ? "2px solid #0f766e" : "2px solid transparent",
              background: "transparent",
              cursor: "pointer",
              whiteSpace: "nowrap",
              color: activeType === type.id ? "#0f766e" : "#475569",
              marginBottom: -2,
            }}
          >
            {type.label}
          </button>
        ))}
        <Link
          to="/settings/professions"
          style={{
            padding: "9px 14px", fontSize: 13, fontWeight: 800, border: "none",
            borderBottom: "2px solid transparent", background: "transparent", color: "#0f766e",
            cursor: "pointer", whiteSpace: "nowrap", marginBottom: -2, textDecoration: "none",
          }}
        >
          + 新しい書類種別を追加
        </Link>
      </div>

      {hasUnsavedDraft && (
        <div style={{ background: "#ecfeff", border: "1px solid #99f6e4", borderRadius: 8, padding: "12px 14px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 900 }}>作成中のテンプレートがあります</div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>{draft?.name ?? "名称未設定"} の未保存内容をこのタブで保持しています。</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="danger" onClick={discardDraft}>削除</Button>
            <Button variant="primary" onClick={resumeDraft}>作成を再開</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p style={{ color: "#94a3b8" }}>読み込み中...</p>
      ) : error ? (
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", borderRadius: 8, padding: 16, fontSize: 13 }}>
          テンプレートAPIの準備中です。新規作成画面とライブプレビューは利用できます。
        </div>
      ) : templates.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 28, maxWidth: 820 }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 18, color: "#0f172a" }}>テンプレートがありません</h2>
          <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
            {getDocTypeLabel(activeType)} は空の状態です。AI生成、PDF取り込み、ライブラリ、手動作成から選べます。
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(120px, 1fr))", gap: 10 }}>
            <Button variant="primary" onClick={goNewWithAI}>AIに作ってもらう</Button>
            <Button onClick={goNew}>PDFから取り込む</Button>
            <Button onClick={() => navigate(`/settings/templates/library?type=${activeType}`)}>ライブラリから選ぶ</Button>
            <Button onClick={goNew}>空から作る</Button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {templates.map((template) => (
            <div
              key={template.id}
              style={{
                background: "#fff",
                border: `1px solid ${template.is_default ? "#0f766e" : "#e2e8f0"}`,
                borderRadius: 8,
                padding: "15px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
                  {template.name}
                  {template.is_default === 1 && <span style={{ marginLeft: 8, color: "#0f766e", fontSize: 11 }}>既定</span>}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  schema v{template.schema_version ?? template.version ?? 2}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {template.is_default !== 1 && <Button onClick={() => setDefaultMutation.mutate(template.id)}>既定にする</Button>}
                <Button variant="danger" disabled={deleteMutation.isPending} onClick={() => deleteTemplate(template)}>削除</Button>
                <Button variant="primary" onClick={() => navigate(`/settings/templates/${template.id}?type=${activeType}`)}>編集</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
